import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Fixed to the bottom whenever the in-page price + buy region is fully outside the viewport
 * (above or below); hides while that region is visible. Hides when the page footer enters view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {HTMLElement | null} */
  #priceContainer = null;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {IntersectionObserver | null} */
  #announcementBarObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {number | null} */
  #scrollRaf = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByFooter = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    this.#setupAnnouncementBarObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mainBottomObserver?.disconnect();
    this.#announcementBarObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Caches the PDP price and buy button blocks, wires scroll/resize, optional footer.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!this.#buyButtonsBlock) return;

    const section = this.closest('.shopify-section');
    this.#priceContainer = /** @type {HTMLElement | null} */ (
      section?.querySelector('.product-details product-price .price-container') ??
        section?.querySelector('product-price .price-container') ??
        section?.querySelector('.product-details .price-container') ??
        null
    );
    if (!this.#priceContainer) {
      this.#priceContainer = this.#buyButtonsBlock;
    }
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    const { signal } = this.#abortController;
    window.addEventListener('scroll', this.#onScrollOrResize, { signal, capture: true, passive: true });
    window.addEventListener('resize', this.#onScrollOrResize, { signal, passive: true });
    this.#onScrollOrResize();

    // Footer: hide sticky as soon as the footer enters (or nears) the viewport
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (footer) {
      this.#mainBottomObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;
          if (entry.isIntersecting) {
            this.#hiddenByFooter = true;
            this.#hideStickyBar();
            this.#clearDock();
          } else {
            this.#hiddenByFooter = false;
            this.#onScrollOrResize();
          }
        },
        { rootMargin: '200px 0px 0px 0px' }
      );
      this.#mainBottomObserver.observe(footer);
    }
  }

  #onScrollOrResize = () => {
    if (this.#scrollRaf) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      this.#syncDockToPriceAndBuy();
    });
  };

  /**
   * Hides the bar when the PDP price + buy region intersects the viewport; otherwise docks to the bottom.
   */
  #syncDockToPriceAndBuy() {
    if (this.#hiddenByFooter) {
      this.#hideStickyBar();
      this.#clearDock();
      return;
    }

    const buy = this.#buyButtonsBlock;
    const price = this.#priceContainer;
    if (!price || !buy) return;

    const p = price.getBoundingClientRect();
    const b = buy.getBoundingClientRect();
    const vh = window.innerHeight;

    const blockTop = Math.min(p.top, b.top);
    const blockBottom = Math.max(p.bottom, b.bottom);
    const blockIntersects = blockBottom > 0 && blockTop < vh;

    if (blockIntersects) {
      this.#hideStickyBar();
      this.#clearDock();
      return;
    }

    this.refs.stickyBar.dataset.dock = 'bottom';
    this.#showStickyBar();
  }

  #clearDock() {
    this.refs.stickyBar.removeAttribute('data-dock');
  }

  /**
   * Observes the announcement bar and adjusts the local `--header-height`
   * custom property to include its height while it is visible in the viewport.
   * When the announcement bar scrolls out of view, the override is removed so
   * the sticky bar falls back to the global header height.
   */
  #setupAnnouncementBarObserver() {
    const announcementBar = /** @type {HTMLElement | null} */ (document.querySelector('.announcement-bar'));
    if (!announcementBar) return;

    this.#announcementBarObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (entry.isIntersecting) {
        const baseHeaderHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
        const announcementHeight = announcementBar.offsetHeight;
        this.style.setProperty('--header-height', `${baseHeaderHeight + announcementHeight}px`);
      } else {
        this.style.removeProperty('--header-height');
      }
    });

    this.#announcementBarObserver.observe(announcementBar);
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    const newHasImage = newStickyBar.getAttribute('data-has-image');
    if (newHasImage != null) {
      this.refs.stickyBar.setAttribute('data-has-image', newHasImage);
    } else {
      this.refs.stickyBar.removeAttribute('data-has-image');
    }
    this.dataset.variantAvailable = variantAvailable;
    this.#onScrollOrResize();

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
